<template>
  <!-- Opened and closed by FarmMap's ArcGIS Expand widget -->
  <div class="layer-toggle-widget">
    <div class="layer-toggle-header">
      <h4>Map Layers</h4>
    </div>

    <div class="layer-toggle-content">
      <div v-for="layer in layerConfig" :key="layer.id" class="layer-item">
        <div class="layer-row">
          <!-- Main layer toggle -->
          <div class="main-layer">
            <label class="layer-checkbox">
              <input 
                type="checkbox" 
                :checked="layer.visible"
                @change="toggleLayer(layer.id, $event.target.checked)"
              />
              <span class="checkmark"></span>
              <span class="layer-name">{{ layer.name }}</span>
            </label>
          </div>
          
          <!-- Label toggle (if exists) -->
          <div v-if="layer.hasLabels" class="label-layer">
            <label class="layer-checkbox label-checkbox">
              <input 
                type="checkbox" 
                :checked="layer.labelsVisible"
                :disabled="!layer.visible"
                @change="toggleLayer(layer.labelId, $event.target.checked)"
              />
              <span class="checkmark"></span>
              <span class="layer-name">Labels</span>
            </label>
          </div>
        </div>
        
        <!-- Opacity slider (optional) -->
        <div v-if="layer.supportsOpacity && layer.visible" class="opacity-control">
          <input 
            type="range" 
            min="0" 
            max="1" 
            step="0.1"
            :value="layer.opacity"
            @input="setLayerOpacity(layer.id, $event.target.value)"
            class="opacity-slider"
          />
          <span class="opacity-value">{{ Math.round(layer.opacity * 100) }}%</span>
        </div>

      </div>
      
      <!-- Global Label Scale Control -->
      <div class="global-scale-control">
        <div class="scale-header">
          <label class="scale-label">Label Visibility Scale:</label>
          <button 
            @click="resetGlobalLabelScale" 
            class="reset-button"
            title="Reset to default"
          >
            Reset
          </button>
        </div>
        <input 
          type="range" 
          min="5000" 
          max="200000" 
          step="5000"
          :value="globalLabelScale"
          @input="setGlobalLabelScale($event.target.value)"
          class="scale-slider"
        />
        <span class="scale-value">1:{{ Math.round(globalLabelScale).toLocaleString() }}</span>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, reactive, onMounted, watch, toRef, computed } from 'vue'

export default {
  name: 'CustomLayerToggle',
  props: {
    layers: {
      type: Object,
      required: true
    },
    soilAnalysisContext: {
      type: Object,
      default: null
    },
    sampleZones: {
      type: [Array, Object],
      default: null
    },
    // True when the map shows DAFM LPIS, which then gets its own toggles
    lpisAvailable: {
      type: Boolean,
      default: false
    },
    // Starting state of the two DAFM LPIS toggles, held by FarmMap
    lpisContextVisible: {
      type: Boolean,
      default: false
    },
    lpisLinkedVisible: {
      type: Boolean,
      default: true
    }
  },
  emits: ['layer-visibility-changed', 'layer-opacity-changed', 'global-label-scale-changed'],
  setup(props, { emit }) {
    const globalLabelScale = ref(15000)
    
    // Create reactive reference to soil analysis context
    const soilAnalysisContext = toRef(props, 'soilAnalysisContext')
    
    // Check if soil analysis results exist - if so, hide parcels by default
    const hasSoilAnalysis = computed(() => {
      // Check the sampleZones prop directly - this contains actual soil analysis results
      const hasSampleZones = !!(
        props.sampleZones && 
        Array.isArray(props.sampleZones) &&
        props.sampleZones.length > 0
      )
      
      
      return hasSampleZones
    })
    
    // Base layer configurations (without soil analysis) - made reactive
    const baseLayerConfigs = reactive([
      {
        id: 'taskLocations',
        name: 'Task Locations',
        visible: true,
        opacity: 1,
        supportsOpacity: false,
        layerIds: ['taskLocationsLayer', 'taskLabelLayer'],
        hasLabels: false // Task labels are integral to task locations
      },
      {
        id: 'farmerLayer',
        name: 'Farms Locations',
        visible: true,
        opacity: 1,
        supportsOpacity: false,
        layerIds: ['farmerLayer'],
        hasLabels: false
      },
      {
        id: 'parcels',
        name: 'Parcels',
        visible: true, // Start visible, watcher will hide if needed
        opacity: 1,
        supportsOpacity: false,
        layerIds: ['farmFieldsLayer'],
        hasLabels: true,
        labelId: 'parcelLabels',
        labelsVisible: true, // Start visible, watcher will hide if needed
        previousLabelsVisible: true, // Show labels when layer is enabled
        labelLayerIds: ['parcelLabelLayer']
      },
      {
        id: 'samples',
        name: 'Samples',
        visible: true,
        opacity: 1,
        supportsOpacity: false,
        layerIds: ['smapSamplePathsLayer', 'sampleGeoreferencesLayer'],
        hasLabels: true,
        labelId: 'samplePathLabels',
        labelsVisible: true,
        previousLabelsVisible: true, // Track previous state for restoration
        labelLayerIds: ['samplePathsLabelLayer', 'manualGeoreferencesLabelLayer']
      }
    ])

    // Soil analysis layer configuration (only included when there are results) - made reactive
    const soilAnalysisLayerConfig = reactive({
      id: 'soilAnalysis',
      name: 'Soil Nutrients',
      visible: true,
      opacity: 1,
      supportsOpacity: false,
      layerIds: ['soilAnalysisLayer', 'soilAnalysisLabelLayer'],
      hasLabels: false // Soil analysis labels are integral
    })

    // DAFM LPIS parcels in grey. The LPIS layers belong to LPISLayerComponent,
    // so FarmMap acts on the change event and holds the state.
    const lpisLayerConfig = reactive({
      id: 'lpis',
      name: 'LPIS Parcels',
      visible: props.lpisContextVisible,
      opacity: 1,
      supportsOpacity: false,
      layerIds: [],
      hasLabels: false
    })

    // The farmer's own linked LPIS parcels, in pink
    const lpisLinkedLayerConfig = reactive({
      id: 'lpisLinked',
      name: "Farmer's LPIS",
      visible: props.lpisLinkedVisible,
      opacity: 1,
      supportsOpacity: false,
      layerIds: [],
      hasLabels: false
    })

    // Create reactive layerConfig that includes soil analysis only when there are results
    const layerConfig = computed(() => {
      const layers = [...baseLayerConfigs]

      if (props.lpisAvailable) {
        layers.push(lpisLinkedLayerConfig, lpisLayerConfig)
      }
      
      // Only include soil analysis layer if there are soil analysis results
      if (hasSoilAnalysis.value) {
        layers.push(soilAnalysisLayerConfig)
      }
      
      return layers
    })

    function toggleLayer(layerId, visible) {
      // Check if this is a label layer ID
      const parentLayer = layerConfig.value.find(l => l.labelId === layerId)
      
      if (parentLayer) {
        // This is a label toggle - update the parent's labelsVisible property
        if (!parentLayer.visible && visible) {
          // Don't allow labels to be shown without the parent layer
          console.warn('Cannot show labels without parent layer enabled')
          return
        }
        
        parentLayer.labelsVisible = visible
        // Also update the previous state tracker when user manually changes labels
        parentLayer.previousLabelsVisible = visible
        
        // Update actual label layers
        if (parentLayer.labelLayerIds) {
          parentLayer.labelLayerIds.forEach(actualLayerId => {
            try {
              const graphicsLayer = props.layers[actualLayerId]
              if (graphicsLayer) {
                graphicsLayer.visible = visible
              }
            } catch (error) {
              console.warn(`Could not set visibility for label layer ${actualLayerId}:`, error)
            }
          })
        }
        
        emit('layer-visibility-changed', { layerId, visible })
        return
      }
      
      // This is a main layer toggle
      const layer = layerConfig.value.find(l => l.id === layerId)
      if (layer) {
        layer.visible = visible
        
        // If turning off main layer, also turn off its labels but remember previous state
        if (!visible && layer.hasLabels) {
          // Save current label state before turning off
          layer.previousLabelsVisible = layer.labelsVisible
          layer.labelsVisible = false
          
          // Update actual label layers
          if (layer.labelLayerIds) {
            layer.labelLayerIds.forEach(actualLayerId => {
              try {
                const graphicsLayer = props.layers[actualLayerId]
                if (graphicsLayer) {
                  graphicsLayer.visible = false
                }
              } catch (error) {
                console.warn(`Could not set visibility for label layer ${actualLayerId}:`, error)
              }
            })
          }
          
          // Emit label change event
          emit('layer-visibility-changed', { layerId: layer.labelId, visible: false })
        }
        
        // If turning on main layer, restore previous label state
        if (visible && layer.hasLabels && layer.previousLabelsVisible) {
          layer.labelsVisible = layer.previousLabelsVisible
          
          // Update actual label layers
          if (layer.labelLayerIds) {
            layer.labelLayerIds.forEach(actualLayerId => {
              try {
                const graphicsLayer = props.layers[actualLayerId]
                if (graphicsLayer) {
                  graphicsLayer.visible = layer.previousLabelsVisible
                }
              } catch (error) {
                console.warn(`Could not set visibility for label layer ${actualLayerId}:`, error)
              }
            })
          }
          
          // Emit label change event
          emit('layer-visibility-changed', { layerId: layer.labelId, visible: layer.previousLabelsVisible })
        }
        
        // Update actual main layers
        if (layer.layerIds) {
          layer.layerIds.forEach(actualLayerId => {
            try {
              const graphicsLayer = props.layers[actualLayerId]
              if (graphicsLayer) {
                graphicsLayer.visible = visible
              }
            } catch (error) {
              console.warn(`Could not set visibility for layer ${actualLayerId}:`, error)
            }
          })
        } else {
          // Handle single layer
          try {
            const graphicsLayer = props.layers[layerId]
            if (graphicsLayer) {
              graphicsLayer.visible = visible
            }
          } catch (error) {
            console.warn(`Could not set visibility for layer ${layerId}:`, error)
          }
        }
        
        emit('layer-visibility-changed', { layerId, visible: layer.visible })
      }
    }

    function setLayerOpacity(layerId, opacity) {
      const layer = layerConfig.value.find(l => l.id === layerId)
      if (layer) {
        layer.opacity = parseFloat(opacity)
        
        // Handle multiple layers (like samples)
        if (layer.layerIds) {
          layer.layerIds.forEach(actualLayerId => {
            try {
              const graphicsLayer = props.layers[actualLayerId]
              if (graphicsLayer) {
                graphicsLayer.opacity = layer.opacity
              }
            } catch (error) {
              console.warn(`Could not set opacity for layer ${actualLayerId}:`, error)
            }
          })
        } else {
          // Handle single layer
          try {
            const graphicsLayer = props.layers[layerId]
            if (graphicsLayer) {
              graphicsLayer.opacity = layer.opacity
            }
          } catch (error) {
            console.warn(`Could not set opacity for layer ${layerId}:`, error)
          }
        }
        
        emit('layer-opacity-changed', { layerId, opacity: layer.opacity })
      }
    }

    function setGlobalLabelScale(scale) {
      globalLabelScale.value = parseFloat(scale)
      emit('global-label-scale-changed', { scale: globalLabelScale.value })
    }

    function resetGlobalLabelScale() {
      const defaultScale = 15000
      globalLabelScale.value = defaultScale
      emit('global-label-scale-changed', { scale: defaultScale })
    }

    // Initialize layer states from actual layers
    onMounted(() => {
      layerConfig.value.forEach(config => {
        try {
          if (config.layerIds) {
            // For combined layers, set the actual layer visibility to match our computed config
            config.layerIds.forEach(layerId => {
              const layer = props.layers[layerId]
              if (layer) {
                layer.visible = config.visible
                layer.opacity = config.opacity
              }
            })
            
            // For layers with labels, also update label layer visibility
            if (config.hasLabels && config.labelLayerIds) {
              config.labelLayerIds.forEach(labelLayerId => {
                const labelLayer = props.layers[labelLayerId]
                if (labelLayer) {
                  labelLayer.visible = config.labelsVisible
                }
              })
            }
          } else {
            // For single layers, set the actual layer to match our config
            const layer = props.layers[config.id]
            if (layer) {
              layer.visible = config.visible
              layer.opacity = config.opacity
            }
          }
        } catch (error) {
          console.warn(`Could not access layer ${config.id}:`, error)
        }
      })
      
      // After initial setup, check soil analysis and update parcels if needed
      updateParcelsVisibilityBasedOnSoilAnalysis()
    })
    
    // Track if we've already initialized parcels based on soil analysis
    const parcelsInitialized = ref(false)
    
    // Function to update parcels visibility based on soil analysis
    function updateParcelsVisibilityBasedOnSoilAnalysis() {
      const shouldHideParcels = hasSoilAnalysis.value
      const parcelsLayer = layerConfig.value.find(l => l.id === 'parcels')
      
      if (parcelsLayer && !parcelsInitialized.value) {
        // Only automatically hide parcels on initial load if there's soil analysis
        // Don't override user preferences after initial setup
        const newVisibility = !shouldHideParcels
        
        parcelsLayer.visible = newVisibility
        
        // If hiding parcels due to soil analysis, also hide labels but preserve ability to toggle them
        if (!newVisibility) {
          parcelsLayer.labelsVisible = false
          // Keep previousLabelsVisible as true so labels can be shown when parcels are enabled
          parcelsLayer.previousLabelsVisible = true
        }
        
        // Update actual ArcGIS layers
        if (parcelsLayer.layerIds) {
          parcelsLayer.layerIds.forEach(actualLayerId => {
            try {
              const graphicsLayer = props.layers[actualLayerId]
              if (graphicsLayer) {
                graphicsLayer.visible = newVisibility
              }
            } catch (error) {
              console.warn(`Could not set visibility for layer ${actualLayerId}:`, error)
            }
          })
        }
        
        // Update actual label layers
        if (parcelsLayer.labelLayerIds) {
          parcelsLayer.labelLayerIds.forEach(actualLayerId => {
            try {
              const graphicsLayer = props.layers[actualLayerId]
              if (graphicsLayer) {
                graphicsLayer.visible = parcelsLayer.labelsVisible
              }
            } catch (error) {
              console.warn(`Could not set visibility for label layer ${actualLayerId}:`, error)
            }
          })
        }
        
        // Emit events to keep parent in sync
        emit('layer-visibility-changed', { 
          layerId: 'parcels', 
          visible: newVisibility 
        })
        emit('layer-visibility-changed', { 
          layerId: 'parcelLabels', 
          visible: parcelsLayer.labelsVisible 
        })
        
        // Mark as initialized to prevent future automatic changes
        parcelsInitialized.value = true
      }
    }
    
    // Watch for changes in soil analysis context and update parcels visibility only on initial setup
    watch(hasSoilAnalysis, (newValue, oldValue) => {
      // Only trigger automatic update if soil analysis state changes from false to true
      // or if we haven't initialized yet
      if ((newValue !== oldValue && newValue) || (!parcelsInitialized.value && newValue)) {
        parcelsInitialized.value = false // Reset to allow re-initialization
        updateParcelsVisibilityBasedOnSoilAnalysis()
      }
      // If soil analysis is removed, reset initialization flag
      else if (!newValue && oldValue) {
        parcelsInitialized.value = false
      }
    })
    
    // Also watch the soilAnalysisContext prop directly
    watch(() => props.soilAnalysisContext, (newContext, oldContext) => {
      // Only trigger if context is newly available or removed
      const hasNewContext = !!newContext && !oldContext
      const contextRemoved = !newContext && !!oldContext
      
      if (hasNewContext || contextRemoved) {
        parcelsInitialized.value = false // Reset to allow re-initialization
        updateParcelsVisibilityBasedOnSoilAnalysis()
      }
    }, { deep: true })
    
    // Watch sampleZones prop for changes in actual soil analysis data
    watch(() => props.sampleZones, (newSampleZones, oldSampleZones) => {
      // Only trigger if sample zones are newly added or removed
      const hasNewSamples = newSampleZones && (!oldSampleZones || oldSampleZones.length !== newSampleZones.length)
      const samplesRemoved = !newSampleZones && !!oldSampleZones
      
      if (hasNewSamples || samplesRemoved) {
        parcelsInitialized.value = false // Reset to allow re-initialization
        updateParcelsVisibilityBasedOnSoilAnalysis()
      }
    }, { deep: true })

    return {
      globalLabelScale,
      layerConfig,
      toggleLayer,
      setLayerOpacity,
      setGlobalLabelScale,
      resetGlobalLabelScale,
      updateParcelsVisibilityBasedOnSoilAnalysis,
      parcelsInitialized
    }
  }
}
</script>

<style scoped>
/* Inside the Expand widget, which draws its own panel */
.layer-toggle-widget {
  background: white;
  min-width: 220px;
  max-height: 70vh;
  overflow-y: auto;
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
  font-size: 14px;
}

.layer-toggle-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px 16px;
  border-bottom: 1px solid #e5e5e5;
  background: #f8f9fa;
  border-radius: 8px 8px 0 0;
}

.layer-toggle-header h4 {
  margin: 0;
  font-size: 14px;
  font-weight: 600;
  color: #333;
}

.layer-toggle-content {
  padding: 8px 0;
  max-height: 400px;
  overflow-y: auto;
}

.layer-item {
  padding: 8px 16px;
  border-bottom: 1px solid #f0f0f0;
}

.layer-item:last-child {
  border-bottom: none;
}

.layer-row {
  display: grid;
  grid-template-columns: 1fr auto;
  gap: 16px;
  align-items: center;
}

.main-layer {
  flex: 1;
}

.label-layer {
  min-width: 80px;
}

.layer-checkbox {
  display: flex;
  align-items: center;
  cursor: pointer;
  user-select: none;
}

.label-checkbox {
  font-size: 13px;
  opacity: 0.8;
}

.label-checkbox .layer-name {
  color: #666;
  font-weight: normal;
}

.label-checkbox:disabled,
.label-checkbox input:disabled ~ .checkmark,
.label-checkbox input:disabled ~ .layer-name {
  opacity: 0.5;
  cursor: not-allowed;
}

.layer-checkbox input[type="checkbox"] {
  position: absolute;
  opacity: 0;
  cursor: pointer;
}

.checkmark {
  height: 16px;
  width: 16px;
  background-color: #f1f1f1;
  border: 2px solid #ddd;
  border-radius: 3px;
  margin-right: 10px;
  position: relative;
  transition: all 0.2s;
}

.layer-checkbox:hover input ~ .checkmark {
  background-color: #e9ecef;
  border-color: #0078d4;
}

.layer-checkbox input:checked ~ .checkmark {
  background-color: #0078d4;
  border-color: #0078d4;
}

.checkmark:after {
  content: "";
  position: absolute;
  display: none;
  left: 4px;
  top: 1px;
  width: 4px;
  height: 8px;
  border: solid white;
  border-width: 0 2px 2px 0;
  transform: rotate(45deg);
}

.layer-checkbox input:checked ~ .checkmark:after {
  display: block;
}

.layer-name {
  color: #333;
  font-weight: 500;
}

.opacity-control {
  display: flex;
  align-items: center;
  margin-top: 8px;
  padding-left: 26px;
}

.opacity-slider {
  flex: 1;
  margin-right: 8px;
  height: 4px;
  border-radius: 2px;
  background: #ddd;
  outline: none;
  -webkit-appearance: none;
  appearance: none;
}

.opacity-slider::-webkit-slider-thumb {
  -webkit-appearance: none;
  appearance: none;
  width: 16px;
  height: 16px;
  border-radius: 50%;
  background: #0078d4;
  cursor: pointer;
}

.opacity-slider::-moz-range-thumb {
  width: 16px;
  height: 16px;
  border-radius: 50%;
  background: #0078d4;
  cursor: pointer;
  border: none;
}

.opacity-value {
  font-size: 12px;
  color: #666;
  min-width: 35px;
  text-align: right;
}

.scale-control {
  display: flex;
  align-items: center;
  margin-top: 8px;
  padding-left: 26px;
}

.scale-label {
  font-size: 11px;
  color: #666;
  margin-right: 8px;
  white-space: nowrap;
}

.scale-slider {
  flex: 1;
  margin-right: 8px;
  height: 4px;
  border-radius: 2px;
  background: #ddd;
  outline: none;
  -webkit-appearance: none;
  appearance: none;
}

.scale-slider::-webkit-slider-thumb {
  -webkit-appearance: none;
  appearance: none;
  width: 16px;
  height: 16px;
  border-radius: 50%;
  background: #28a745;
  cursor: pointer;
}

.scale-slider::-moz-range-thumb {
  width: 16px;
  height: 16px;
  border-radius: 50%;
  background: #28a745;
  cursor: pointer;
  border: none;
}

.scale-value {
  font-size: 12px;
  color: #666;
  min-width: 35px;
  text-align: right;
}

.global-scale-control {
  padding: 12px 16px;
  border-top: 1px solid #e5e5e5;
  background: #f8f9fa;
  margin-top: 8px;
}

.global-scale-control .scale-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 6px;
}

.global-scale-control .scale-label {
  font-size: 12px;
  color: #666;
  font-weight: 500;
  margin: 0;
}

.global-scale-control .reset-button {
  background: #007bff;
  color: white;
  border: none;
  border-radius: 4px;
  padding: 2px 8px;
  font-size: 10px;
  cursor: pointer;
  transition: background-color 0.2s;
}

.global-scale-control .reset-button:hover {
  background: #0056b3;
}

.global-scale-control .scale-slider {
  width: 100%;
  margin-bottom: 4px;
}

.global-scale-control .scale-value {
  font-size: 11px;
  color: #888;
  text-align: center;
  display: block;
}
</style>